import { FC, useState } from "react";
import MenuList, { IMenuItem } from "../../MenuList";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CSSObject,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Theme,
} from "@mui/material";
import IsGranted from "../../../../guards/IsGranted";
import { compactMenuStyles, styles } from "../styles";
import MuiDrawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import useAuth from "../../../../hooks/useAuth";
import { IconSideBarHeader } from "./CompactSideBarHeader";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useTheme } from "@mui/material/styles";
import { primary } from "../../../../theme/themeColors";
import { Span } from "../../../Typography";
import useInstitution from "../../../../hooks/useInstitution";

export const CompactMenu: FC<{
  item?: any | IMenuItem;
  isIconMenu: boolean;
  setIsCompactMenu: (value: boolean) => void;
}> = ({ item, isIconMenu, setIsCompactMenu }) => {
  const theme = useTheme();
  const { user }: any = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const business: any = useInstitution();

  const drawerWidth = "20%";
  const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  });

  const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
      width: `calc(${theme.spacing(8)} + 1px)`,
    },
  });

  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
  })(({ theme }) => ({
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          "& .MuiDrawer-paper": openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          "& .MuiDrawer-paper": closedMixin(theme),
        },
      },
    ],
  }));

  const isRightUser = (requiredUsers?: string[]) => {
    if (!requiredUsers) return true;
    return requiredUsers.includes(user?.type);
  };

  const handleClickLink = (menuItem: IMenuItem) => {
    navigate(menuItem.path);
  };

  const isActive = (path: string) => {
    return location?.pathname === path;
  };

  const renderStaffUpgradeBadge = (item: IMenuItem) => {
    if (item.title !== "Staff") return null;
    if (business?.settings?.usage?.can_create_staff !== false) return null;

    return (
      <Span
        sx={{
          marginLeft: "auto",
          backgroundColor: "#FEC90D",
          padding: "2px",
          fontSize: "9px",
          borderRadius: "3px",
        }}
      >
        Upgrade
      </Span>
    );
  };
  const renderBranchUpgradeBadge = (item: IMenuItem) => {
    if (item.title !== "Branches") return null;
    if (business?.settings?.usage?.can_create_branches !== false) return null;

    return (
      <Span
        sx={{
          marginLeft: "auto",
          backgroundColor: "#FEC90D",
          padding: "2px",
          fontSize: "9px",
          borderRadius: "3px",
        }}
      >
        Upgrade
      </Span>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          backgroundColor: theme.palette.primary.main,
        }}
      >
        <Drawer variant="permanent" open={isIconMenu} sx={customStyles?.drawer}>
          <Box sx={{ display: "flex", width: "100%" }}>
            <Box sx={customStyles?.iconWrapper}>
              <IconSideBarHeader />
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <List>
                  {MenuList.map((item, index) => (
                    <IsGranted
                      key={`${item.title}-main-${index}`}
                      permissions={item.permissions || []}
                    >
                      <ListItem
                        key={index}
                        disablePadding
                        sx={{ display: "block" }}
                      >
                        <ListItemButton
                          sx={[
                            styles.menuItemButton,
                            {
                              display: isRightUser(item.users)
                                ? "flex"
                                : "none",
                            },
                          ]}
                        >
                          <ListItemIcon sx={styles.menuItemIcon}>
                            <item.Icon />
                          </ListItemIcon>
                        </ListItemButton>
                      </ListItem>
                    </IsGranted>
                  ))}
                </List>
              </Box>
            </Box>
            <Box sx={customStyles?.menuWrapper}>
              <Button
                component={Box}
                size="small"
                sx={compactMenuStyles.backToMenuButton}
                onClick={() => setIsCompactMenu(false)}
              >
                <Box>
                  <KeyboardArrowLeftIcon
                    fontSize="small"
                    sx={{ alignSelf: "center", color: "#ffffff" }}
                  />
                </Box>
                <Box>
                  <Typography
                    fontSize={11}
                    fontWeight={800}
                    sx={{
                      alignSelf: "center",
                      marginTop: 0.2,
                      color: "#ffffff",
                    }}
                  >
                    {" "}
                    BACK TO MAIN MENU{" "}
                  </Typography>
                </Box>
              </Button>

              {item?.categories?.map((category: any, index: number) => (
                <Box key={index} sx={{ marginTop: index == 0 ? 0 : 2 }}>
                  <Typography
                    fontWeight={700}
                    fontSize={13}
                    sx={{ textTransform: "uppercase", color: "#000" }}
                  >
                    {category?.toUpperCase()}
                  </Typography>

                  {item?.subMenu
                    ?.filter((menu: any) => menu?.category == category)
                    .map((subMenu: any, menuIndex: number) => (
                      <IsGranted
                        key={`${subMenu.title}sub-${menuIndex}`}
                        permissions={subMenu?.permissions || []}
                        requiredUsers={subMenu.users}
                      >
                        <ListItemButton
                          className={isActive(subMenu.path) ? "active" : ""}
                          sx={[compactMenuStyles.menuItemButton]}
                          onClick={() => {
                            handleClickLink(subMenu);
                          }}
                        >
                          <ListItemText
                            sx={compactMenuStyles.menuItemText}
                            primary={subMenu?.title}
                          />
                          {subMenu.hasNewFeature && (
                            <Span
                              sx={{
                                marginLeft: "auto",
                                backgroundColor: "#FFE7CA",
                                padding: "2px",
                                fontSize: "9px",
                                borderRadius: "3px",
                              }}
                            >
                              New
                            </Span>
                          )}
                          {renderStaffUpgradeBadge(subMenu)}
                          {renderBranchUpgradeBadge(subMenu)}
                        </ListItemButton>
                      </IsGranted>
                    ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Drawer>
      </Box>
    </>
  );
};

const customStyles = {
  drawer: {
    "& .MuiPaper-root": {
      width: {
        lg: "20%",
        md: "20%",
        xs: "83%",
        sm: "77%",
      },
    },
  },

  iconWrapper: {
    width: "25%",
    display: "flex",
    justifyContent: "center",
    background: primary.main,
    flexDirection: "column",
  },
  menuWrapper: {
    width: "75%",
    padding: 2,
    paddingRight: 0,
    // backgroundColor: "#FFF5E9",
    backgroundColor: "#FFFFF0",
  },
};
